import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import FeatureChannelImgSrc from '../../../../../assets/images/developers/featureChannel.png';
import FeatureInboxSidePanelImgSrc from '../../../../../assets/images/developers/featureInboxSidePanel.png';
import {useLinkNavigation} from '../../../../../components/common/navigation/useLinkNavigation';
import {Pill} from '../../../../../components/pill/pill';
import {computeIsLocalApplicationFeatureActive} from '../../../../../helpers/localApplicationHelpers';
import {
  LocalApplicationFeaturesEnum,
  LocalApplicationModel,
} from '../../../../../models/api/localApplicationModel';
import {buildSettingsDevelopersPath} from '../../../../../models/url/settingsUrlHelpers';
import {SettingsDevelopersPagesEnum, SettingsRootPagesEnum} from '../../../../../models/url/settingsUrlModel';

/*
 * Constants.
 */

export const featureDetailsRenderMap: {
  [key in LocalApplicationFeaturesEnum]: {
    backgroundUrl: string;
    title: React.ReactNode;
    description: React.ReactNode;
    createButton: React.ReactNode;
    editButton: React.ReactNode;
  };
} = {
  [LocalApplicationFeaturesEnum.CHANNEL]: {
    backgroundUrl: FeatureChannelImgSrc,
    title: <Trans id="settings-developers-app-feature-channel-title">Channel type</Trans>,
    description: (
      <Trans id="settings-developers-app-feature-channel-description">
        Connect an external message source (SMS providers, social channels, live chat widgets, etc.) to allow
        users to send and receive messages directly from Front.
      </Trans>
    ),
    createButton: (
      <Trans id="settings-developers-app-feature-channel-create-action">Create channel type</Trans>
    ),
    editButton: <Trans id="settings-developers-app-feature-channel-edit-action">Edit channel type</Trans>,
  },
  [LocalApplicationFeaturesEnum.INBOX_SIDEPANEL]: {
    backgroundUrl: FeatureInboxSidePanelImgSrc,
    title: <Trans id="settings-developers-app-feature-inbox-sidepanel-title">Plugin</Trans>,
    description: (
      <Trans id="settings-developers-app-feature-inbox-sidepanel-description">
        Embed custom UI into Front&apos;s sidebar to make it easy for users to gather context and automate
        repetitive actions.
      </Trans>
    ),
    createButton: (
      <Trans id="settings-developers-app-feature-inbox-sidepanel-create-action">Create plugin</Trans>
    ),
    editButton: <Trans id="settings-developers-app-feature-inbox-sidepanel-edit-action">Edit plugin</Trans>,
  },
};

/*
 * Props.
 */

interface SettingsDevelopersApplicationFeatureCardProps {
  feature: LocalApplicationFeaturesEnum;
  application: LocalApplicationModel;
}

/*
 * Style.
 */

const StyledFeatureCardWrapperDiv = styled.div`
  height: 437px;
  flex: 0 0 394px;
  background: ${(p) => p.theme.greys.white};
  ${(p) =>
    boxShadow(p.theme, `0px 0px 1px ${p.theme.alphas.black50}, 0px 3px 10px ${p.theme.alphas.black30}`)};
  border-radius: 16px;
  overflow: hidden;
`;

interface StyledFeatureImgDivProps {
  $backgroundUrl?: string;
}

const StyledFeatureImgDiv = styled.div<StyledFeatureImgDivProps>`
  height: 263px;

  ${(p) => css`
    background: url(${p.$backgroundUrl});
    background-color: ${p.theme.greys.shade20};
    background-position: center;
    background-size: cover;
  `};
`;

const StyledContentWrapperDiv = styled.div`
  padding: 20px;
`;

const StyledHeaderWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  margin-bottom: 8px;
`;

const StyledHeaderTitleDiv = styled.div`
  flex: 1;
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
  line-height: 19px;
`;

const StyledActivePill = styled(Pill)`
  height: 20px;
  line-height: 14px;
  font-size: ${fontSizes.verySmall};
`;

const StyledDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  line-height: 20px;
  margin-bottom: 16px;
`;

const StyledActionWrapperDiv = styled.div`
  display: flex;
`;

/*
 * Component.
 */

export const SettingsDevelopersApplicationFeatureCard: FC<SettingsDevelopersApplicationFeatureCardProps> = (
  props,
) => {
  const {feature, application} = props;
  const theme = useUserTheme();
  const {navigateToLink} = useLinkNavigation();

  const isActive = computeIsLocalApplicationFeatureActive(feature, application);

  return (
    <StyledFeatureCardWrapperDiv>
      <StyledFeatureImgDiv $backgroundUrl={featureDetailsRenderMap[feature].backgroundUrl} />
      <StyledContentWrapperDiv>
        <StyledHeaderWrapperDiv>
          <StyledHeaderTitleDiv>{featureDetailsRenderMap[feature].title}</StyledHeaderTitleDiv>
          {isActive && (
            <StyledActivePill
              backgroundColor={theme.palette.blue.shade20}
              color={theme.palette.blue.shade40}
              size={VisualSizesEnum.MEDIUM}
            >
              <Trans id="settings-developers-app-feature-active-pill">Active</Trans>
            </StyledActivePill>
          )}
        </StyledHeaderWrapperDiv>
        <StyledDescriptionDiv>{featureDetailsRenderMap[feature].description}</StyledDescriptionDiv>
        <StyledActionWrapperDiv>
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            onClick={() =>
              navigateToLink(
                buildSettingsDevelopersPath({
                  rootPage: SettingsRootPagesEnum.DEVELOPERS,
                  subPage: SettingsDevelopersPagesEnum.APPLICATION_FEATURES,
                  applicationId: application.id,
                  feature,
                }),
              )
            }
          >
            {isActive
              ? featureDetailsRenderMap[feature].editButton
              : featureDetailsRenderMap[feature].createButton}
          </Button>
        </StyledActionWrapperDiv>
      </StyledContentWrapperDiv>
    </StyledFeatureCardWrapperDiv>
  );
};
